import type { ContactActivity } from '@/shared/types/contacts';

// Changes this close to the previous one, by the same actor, read as one edit.
export const FIELD_CHANGE_GROUP_GAP_MS = 5 * 60 * 1000;

export type ActivityItem =
  { kind: 'single'; activity: ContactActivity } | { kind: 'group'; activities: ContactActivity[] };

/** The changed field of a `contact_events` row. Older audit rows name none and stay single. */
export const changedField = (activity: ContactActivity): string | undefined => {
  if (activity.type !== 'contact_updated' && activity.type !== 'deceased_set') {
    return undefined;
  }
  const field = activity.summary?.values?.field;
  return typeof field === 'string' ? field : undefined;
};

const sameActor = (a: ContactActivity, b: ContactActivity): boolean =>
  a.actor?.kind === b.actor?.kind && a.actor?.id === b.actor?.id && a.actor?.name === b.actor?.name;

const joins = (previous: ContactActivity, next: ContactActivity): boolean =>
  changedField(next) !== undefined &&
  sameActor(previous, next) &&
  Math.abs(Date.parse(previous.timestamp) - Date.parse(next.timestamp)) <=
    FIELD_CHANGE_GROUP_GAP_MS;

/**
 * Folds consecutive field changes by one actor, each within `FIELD_CHANGE_GROUP_GAP_MS` of the
 * one before, into a group. Every other row, and a lone change, stays single.
 */
export const groupActivities = (activities: ContactActivity[]): ActivityItem[] => {
  const runs: ContactActivity[][] = [];
  for (const activity of activities) {
    const run = runs.at(-1);
    const last = run?.at(-1);
    if (run && last && changedField(last) !== undefined && joins(last, activity)) {
      run.push(activity);
    } else {
      runs.push([activity]);
    }
  }
  return runs.map((run) =>
    run.length > 1 ? { kind: 'group', activities: run } : { kind: 'single', activity: run[0] }
  );
};
